import type { GridApi, ICellRendererParams, IRowNode } from 'ag-grid-community';

import type { Organization } from '@/pages/Organizations';

import type { LimaSourcesResponseModal } from '../DevicesTable';

/**
 * Base tree node containing common fields shared across all AG Grid tree implementations
 */
export type BaseTreeNode = {
  id: string;
  path: string[];
  type: string;
  name: string;
  organization?: string;
  status?: string;
  statusLabel?: string;
  lastSeen?: string;
  hasChildren?: boolean;
  level?: number;
};

/**
 * Type of cloud source tree node - either a parent source or a child provider
 */
export type CloudSourceTreeNodeType = 'source' | 'provider';

/**
 * Cloud source tree node for AG Grid tree data
 * Combines source/provider data with tree structure
 */
export type CloudSourceTreeNode = BaseTreeNode & {
  type: CloudSourceTreeNodeType;
  ingestMethod?: string;
  deviceName?: string;
  active?: boolean;
  categoryFailedTooltip?: string | null;

  // Original data references
  sourceData?: LimaSourcesResponseModal;
  providerData?: LimaSourcesResponseModal;

  // Additional fields
  allowSourceActivation?: boolean;
  is_selected?: boolean;
};

/**
 * Menu action types available for cloud sources
 */
export type ActionMenuType =
  | 'edit'
  | 'delete'
  | 'activate'
  | 'deactivate'
  | 'resubmit';

/**
 * Options for building cloud source tree data
 */
export type CloudSourceTreeDataBuilderOptions = {
  selectedOrganizationId: number;
  includeMicrosoftProviders?: boolean;
};

/**
 * Tree level constants for cloud source hierarchy
 */
export const CLOUD_SOURCE_TREE_LEVEL = {
  SOURCE: 0,
  PROVIDER: 1
} as const;

/**
 * Configuration for row class styling
 */
export type RowClassConfig = {
  /**
   * Mapping of level numbers to CSS class names
   */
  levelClassMap: Record<number, string>;
  /**
   * CSS class for last child rows
   */
  lastChildClass: string;
  /**
   * CSS class for selected rows
   */
  selectedRowClass: string;
  /**
   * CSS class for rows with detail pane open
   */
  detailOpenRowClass?: string;
};

/**
 * Context object passed to AG Grid for access in cell renderers and row classes
 */
export type CloudSourceGridContext = {
  setIsContextMenuOpen?: (isOpen: boolean) => void;
  selectedSourceUid?: string;
  hasUpdatePermission?: boolean;
  selectedOrgData?: Organization;
  onMenuClick?: (action: ActionMenuType, row: CloudSourceTreeNode) => void;
};

export type CloudSourceCellRendererParams = ICellRendererParams & {
  data: CloudSourceTreeNode | undefined;
  node: IRowNode<CloudSourceTreeNode>;
  context: CloudSourceGridContext;
};

export type CloudSourcesTableProps = {
  isCustomerCloudSource: boolean;
};

export type ReconcileCloudSourcesDetailOptions = {
  api: GridApi<CloudSourceTreeNode>;
  selectedRowId?: string;
  filteredRowIds: Set<string>;
  onMissingSelection?: () => void;
};
